import {
  ALERT_COMMENTARY_MIN_LENGTH,
  Alert,
  AlertReferencePanelTab,
  AlertsCommentary,
  alertHasResolvedComment,
  alertMatchesFocusIdentity,
  alertMatchesReferencePanelTab,
  alertRequiresExplanationFinalization,
  buildAlertsCommentaryFormValues,
  dismissedAlertMatches,
  expected,
  getAlertCommentaryKey,
  getAlertCompType,
  getAlertReferencePanelTab,
  getAlertTypeLabel,
  getOrderedAlertPageGroups,
  isAlertExplanationFinalizedForTab,
  nonDismissibleAlertTypes,
  normalizePairedCostRouteItem,
  toAlertFocusIdentity,
} from '@aloft/toolkit-shared';
import { Button, Flex, Input, Tooltip, Typography, colors } from '@aloft/toolkit-sui';

import { debounce, isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { CompTypeBadge } from '../../../components/CompTypeBadge.tsx';
import {
  getPropertyIdFromPropertyGridCellKey,
  parsePropertyGridCellKey,
} from '../../../components/PropertyGrid/types.ts';
import { dismissAlertToasts } from '../../../hooks/useToaster/dismissAllToasts.ts';
import { useZodForm } from '../../../hooks/useZodForm.ts';
import {
  APPRAISIFY_FIX_DESTINATION_BLOCKED_TOOLTIP,
  routeToStepMap,
} from '../../../hooks/validation/consts.ts';
import { useAppraisifyNavigation } from '../../../hooks/validation/useAppraisifyNavigation.ts';
import { cancelDismissAlert, dismissAlert } from '../../../redux/features/alertsSlice.ts';
import { setCommentaryV2 } from '../../../redux/features/commentarySlice.ts';
import { setActiveID, setFocusedAlertType } from '../../../redux/features/referenceBarUISlice.ts';
import { selectAlerts } from '../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsCommentaryLoaded } from '../../../redux/features/selectors/commentarySelectors/selectIsCommentaryLoaded.ts';
import {
  selectIsInspectifyOrder,
  selectIsInternalValuation,
  selectIsMockOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectReferenceBarUI } from '../../../redux/features/selectors/referenceBarUISelectors.ts';
import { selectTriggeredAlerts } from '../../../redux/features/selectors/uiSelectors.ts';
import {
  addFinalizedExplanation,
  addPendingDismissal,
  removeFinalizedExplanation,
  removePendingDismissal,
  setAivArvActiveTab,
  setFocusedOrderDetailsFieldKey,
  setFocusedPropertyGridCellKey,
  setFocusedRequiredFieldKey,
} from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { store } from '../../../redux/store.ts';
import { AlertResolvedIcon } from '../../alerts/AlertResolvedIcon.tsx';
import { AlertSeverityIcon } from '../../alerts/AlertSeverityIcon.tsx';
import {
  focusCostApproachSliders,
  isCostApproachAlertFixRoute,
} from '../../alerts/costApproachSliderFocus.ts';
import { parseRequiredFieldTargetCellKey } from '../../alerts/requiredFieldFocus.ts';
import {
  shouldPreserveAlertHoverPreview,
  syncAlertHoverPreview,
} from '../../alerts/syncAlertHoverPreview.ts';
import { parseOrderDetailsTargetCellKey } from '../../orderDetails/orderDetailsFieldFocus.ts';
import { propertyIdToReferenceViewId } from '../index.ts';
import {
  finalizeAlertCommentaryValue,
  trimAlertCommentaryFormValue,
} from './finalizeAlertCommentary.ts';
import { getAlertPageLabel } from './getAlertPageLabel.ts';
import { useAlerts } from './hooks/useAlerts.tsx';

import './AlertsContent.scss';

const EMPTY_TAB_MESSAGES: Record<AlertReferencePanelTab, string> = {
  active: '🎉 Congrats, no alerts remain!',
  explained: 'No explained alerts.',
  ignored: 'No ignored alerts.',
  all: 'No alerts.',
};

const scrollFocusedAlertIntoView = (element: HTMLElement) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
};

const AlertItem = ({
  alert,
  shouldFocusCommentary,
  onCommentaryFocused,
  onExplain,
  isPendingExplain,
  isExplanationFinalized,
  onFinalizeExplanation,
  onCancelExplain,
  onUndoExplanation,
}: {
  alert: Alert;
  shouldFocusCommentary?: boolean;
  onCommentaryFocused?: () => void;
  onExplain?: () => void;
  isPendingExplain: boolean;
  isExplanationFinalized: boolean;
  onFinalizeExplanation: () => void;
  onCancelExplain: () => void;
  onUndoExplanation?: () => void;
}) => {
  const {
    type,
    status,
    message,
    propertyID,
    fixingInfo: { tips, pairedCostRouteItem, fixingMethod, targetCellKey, commentAlwaysRequired },
  } = alert;
  const alertCommentaryKey = getAlertCommentaryKey(alert);
  const alertCompType = getAlertCompType(alert);

  const { severity } = alert;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { isRouteValidationStepEnabled } = useAppraisifyNavigation();

  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const {
    focusedAlertType,
    focusedAlertPropertyID,
    focusedAlertTargetCellKey,
    hoveredAlertType,
    hoveredAlertPropertyID,
    hoveredAlertTargetCellKey,
  } = useAppSelector(selectReferenceBarUI);
  const focusedIdentity = useMemo(
    () =>
      focusedAlertType
        ? {
            type: focusedAlertType,
            propertyID: focusedAlertPropertyID,
            targetCellKey: focusedAlertTargetCellKey,
          }
        : null,
    [focusedAlertPropertyID, focusedAlertTargetCellKey, focusedAlertType],
  );
  const hoveredIdentity = useMemo(
    () =>
      hoveredAlertType
        ? {
            type: hoveredAlertType,
            propertyID: hoveredAlertPropertyID,
            targetCellKey: hoveredAlertTargetCellKey,
          }
        : null,
    [hoveredAlertPropertyID, hoveredAlertTargetCellKey, hoveredAlertType],
  );
  const isFocused = alertMatchesFocusIdentity(alert, focusedIdentity);
  const isHoveredFromAlert = alertMatchesFocusIdentity(alert, hoveredIdentity);
  const isFocusedToastHovered = isFocused && isHoveredFromAlert;
  const itemRef = useRef<HTMLDivElement>(null);
  const commentaryInputRef = useRef<HTMLInputElement>(null);
  const alertErrorColor = colors.alertError;

  const { control } = useFormContext<AlertsCommentary>();
  const commentValue = useWatch({ control, name: alertCommentaryKey });
  const trimmedComment = trimAlertCommentaryFormValue(commentValue) ?? '';
  const hasSufficientComment = trimmedComment.length >= ALERT_COMMENTARY_MIN_LENGTH;
  const hasResolvedComment = alertHasResolvedComment(
    alert,
    trimmedComment !== '' ? { [alertCommentaryKey]: trimmedComment } : undefined,
  );

  useEffect(() => {
    if (!isFocused || !itemRef.current) {
      return;
    }
    scrollFocusedAlertIntoView(itemRef.current);
  }, [isFocused]);

  useEffect(() => {
    if (!shouldFocusCommentary) {
      return;
    }
    window.requestAnimationFrame(() => {
      commentaryInputRef.current?.focus();
      onCommentaryFocused?.();
    });
  }, [onCommentaryFocused, shouldFocusCommentary]);

  useEffect(() => {
    const element = itemRef.current;
    if (!element) {
      return;
    }
    const handleMouseLeave = (event: MouseEvent) => {
      if (element.contains(event.relatedTarget as Node)) {
        return;
      }
      if (shouldPreserveAlertHoverPreview(event.relatedTarget as HTMLElement | null)) {
        return;
      }
      syncAlertHoverPreview(dispatch, null);
    };
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => element.removeEventListener('mouseleave', handleMouseLeave);
  }, [dispatch]);

  const isDismissed = status === 'dismissed' || isPendingExplain;
  const isNonDismissable = nonDismissibleAlertTypes.includes(type);
  const canDismiss = !isNonDismissable;
  const hasAction = fixingMethod.includes('action');
  const hasComment = fixingMethod.includes('comment');
  const showFixNow = hasAction && isInspectifyOrder && pairedCostRouteItem && status === 'active';
  const fixDestinationRoute = normalizePairedCostRouteItem(pairedCostRouteItem, routeToStepMap);
  const isFixDestinationAccessible =
    !fixDestinationRoute || isRouteValidationStepEnabled(fixDestinationRoute);
  const showIgnore = !isDismissed && canDismiss && severity === 'warning' && isInspectifyOrder;
  const showExplain =
    !isDismissed && canDismiss && severity === 'error' && hasComment && isInspectifyOrder;
  const requiresExplanationWorkflow = alertRequiresExplanationFinalization(alert);
  const canExplainWithDone = canDismiss && hasComment && severity === 'error' && isInspectifyOrder;
  const showCommentaryInput =
    (isDismissed || commentAlwaysRequired) && alert.severity !== 'warning';
  const showDone =
    (isPendingExplain && canExplainWithDone) ||
    (requiresExplanationWorkflow && !isExplanationFinalized);
  const showCancelExplain = isPendingExplain && (requiresExplanationWorkflow || canExplainWithDone);
  const showUndoExplain =
    canDismiss && isExplanationFinalized && requiresExplanationWorkflow && isInspectifyOrder;
  const isAlertItemClickable = Boolean(showFixNow && isFixDestinationAccessible);

  const focusCostApproachSlidersIfNeeded = () => {
    if (
      propertyID &&
      !targetCellKey &&
      isCostApproachAlertFixRoute(fixDestinationRoute ?? pairedCostRouteItem)
    ) {
      focusCostApproachSliders(dispatch, propertyID);
    }
  };

  const focusAlertTarget = (activate = false) => {
    if (!targetCellKey) {
      focusCostApproachSlidersIfNeeded();
      return;
    }
    const orderDetailsField = parseOrderDetailsTargetCellKey(targetCellKey);
    if (orderDetailsField) {
      dispatch(setFocusedOrderDetailsFieldKey(orderDetailsField));
      return;
    }
    const requiredField = parseRequiredFieldTargetCellKey(targetCellKey);
    if (requiredField) {
      dispatch(setFocusedRequiredFieldKey(requiredField.fieldName));
      return;
    }
    const parsedCellKey = parsePropertyGridCellKey(targetCellKey);
    if (parsedCellKey) {
      const propertyId = getPropertyIdFromPropertyGridCellKey(parsedCellKey);
      if (propertyId !== 'subject') {
        dispatch(setActiveID(propertyIdToReferenceViewId(propertyId)));
      }
      dispatch(
        activate
          ? setFocusedPropertyGridCellKey({ key: parsedCellKey, activate: true })
          : setFocusedPropertyGridCellKey(parsedCellKey),
      );
    }
  };

  const focusAlert = () => {
    dispatch(setFocusedAlertType(toAlertFocusIdentity(alert)));
    dismissAlertToasts();
    focusAlertTarget();
  };

  const navigateToAlertFix = () => {
    dispatch(setFocusedAlertType(toAlertFocusIdentity(alert)));
    if (alertCompType) {
      dispatch(setAivArvActiveTab(alertCompType));
    }
    dismissAlertToasts();
    focusAlertTarget(true);
    navigate(`/order/${expected(orderId)}/paired-cost/${pairedCostRouteItem}`);
  };

  const handleFixNow = () => navigateToAlertFix();

  const itemClassName = [
    'alerts-sheet-alert-item',
    isAlertItemClickable ? 'alerts-sheet-alert-item--clickable' : undefined,
    isFocused ? 'alerts-sheet-alert-item--focused' : undefined,
    isFocused ? `alerts-sheet-alert-item--${severity}` : undefined,
    isFocusedToastHovered ? 'alerts-sheet-alert-item--focused-toast-hovered' : undefined,
    isHoveredFromAlert && !isFocused ? 'alerts-sheet-alert-item--hovered' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Flex
      ref={itemRef}
      key={alertCommentaryKey}
      className={itemClassName}
      flexDirection="column"
      gap="8px"
      alignItems="flex-end"
      width="100%"
      padding="4px"
      boxSizing="border-box"
      onClick={isAlertItemClickable ? focusAlert : undefined}
      onMouseEnter={() => syncAlertHoverPreview(dispatch, alert)}
    >
      <Flex gap="8px" alignItems="flex-start" width="100%">
        {hasResolvedComment ? <AlertResolvedIcon /> : <AlertSeverityIcon severity={severity} />}
        <Flex flex={1} minWidth={0} flexDirection="column" gap="4px">
          <Flex alignItems="center" gap="8px" flexWrap="wrap">
            <Typography variant="h4">{getAlertTypeLabel(type)}</Typography>
            {alertCompType && <CompTypeBadge compType={alertCompType} />}
          </Flex>
          <Typography variant="body1">{message}</Typography>
        </Flex>
      </Flex>

      <Flex flexDirection="column" width="100%" paddingLeft="32px">
        <Typography variant="body2" color="surfaceAccent4">
          {tips}
        </Typography>
      </Flex>

      {showCommentaryInput && (
        <Controller
          name={alertCommentaryKey}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Flex
              width="100%"
              flexDirection="column"
              gap="4px"
              paddingLeft="32px"
              onClick={(event) => event.stopPropagation()}
            >
              <Input
                ref={commentaryInputRef}
                type="multiline"
                resize="vertical"
                __dangerDoNotUseSx={error ? { borderColor: alertErrorColor } : undefined}
                value={value ?? ''}
                onClear={() => onChange('')}
                onChange={onChange}
                placeholderText="Text entered here will appear in the final report. Please enter accurate, professional content."
              />
              <Flex justifyContent="space-between" alignItems="center" width="100%">
                {error ? (
                  <Typography variant="body2" color="alertError">
                    Required
                  </Typography>
                ) : (
                  <Flex />
                )}
                <Flex gap="8px">
                  {showCancelExplain && (
                    <Button variant="inline" size="sm" text="Cancel" onClick={onCancelExplain} />
                  )}
                  {showUndoExplain && (
                    <Button
                      variant="inline"
                      size="sm"
                      text="Cancel"
                      onClick={() => onUndoExplanation?.()}
                    />
                  )}
                  {showDone && (
                    <Button
                      size="sm"
                      text="Done"
                      disabled={!hasSufficientComment}
                      onClick={onFinalizeExplanation}
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
          )}
        />
      )}

      {!isDismissed && (showFixNow || showIgnore || showExplain) && (
        <Flex
          width="100%"
          gap="8px"
          justifyContent="flex-end"
          onClick={(event) => event.stopPropagation()}
        >
          {showFixNow &&
            (isFixDestinationAccessible ? (
              <Button size="sm" text="Fix Now" onClick={handleFixNow} />
            ) : (
              <Tooltip
                label={APPRAISIFY_FIX_DESTINATION_BLOCKED_TOOLTIP}
                placement="bottom"
                hasArrow
                openDelay={200}
                bg="textPrimary"
                color="textOffset"
              >
                <Button size="sm" text="Fix Now" disabled onClick={() => undefined} />
              </Tooltip>
            ))}
          {showIgnore && (
            <Button
              variant="secondary"
              size="sm"
              text="Ignore"
              onClick={() => dispatch(dismissAlert({ type, propertyID }))}
            />
          )}
          {showExplain && (
            <Button
              variant="secondary"
              size="sm"
              text="Explain"
              onClick={() => {
                onExplain?.();
              }}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export const AlertsContent = ({
  alerts,
  activeTab,
  setActiveTab,
}: {
  alerts: ReturnType<typeof useAlerts>;
  activeTab: AlertReferencePanelTab;
  setActiveTab: Dispatch<SetStateAction<AlertReferencePanelTab>>;
}) => {
  const dispatch = useAppDispatch();
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const isMockOrder = useAppSelector(selectIsMockOrder);
  const isInternalValuation = useAppSelector(selectIsInternalValuation);
  const triggeredAlertTypes = useAppSelector(selectTriggeredAlerts);
  const { pathname } = useLocation();
  const { getCurrentAppraisifyRoute } = useAppraisifyNavigation();
  const currentRoute = getCurrentAppraisifyRoute(pathname);
  const { focusedAlertType, focusedAlertPropertyID, focusedAlertTargetCellKey } =
    useAppSelector(selectReferenceBarUI);
  const reduxCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const pendingDismissals = useAppSelector((state) => state.ui.pendingDismissals);
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = useMemo(
    () => ({ dismissedAlerts, finalizedExplanations, pendingDismissals }),
    [dismissedAlerts, finalizedExplanations, pendingDismissals],
  );
  const [commentaryFocusKey, setCommentaryFocusKey] = useState<string | null>(null);

  const isCommentaryLoaded = useAppSelector(selectIsCommentaryLoaded);
  const treatAsInspectifyOrder = isInspectifyOrder && !isMockOrder && !isInternalValuation;

  const handleExplain = useCallback(
    (alert: Alert) => {
      dispatch(addPendingDismissal({ type: alert.type, propertyID: alert.propertyID }));
      dispatch(setFocusedAlertType(toAlertFocusIdentity(alert)));
      setCommentaryFocusKey(getAlertCommentaryKey(alert));
      setActiveTab('active');
    },
    [dispatch, setActiveTab],
  );

  const handleCancelExplain = useCallback(
    (alert: Alert) => {
      dispatch(removePendingDismissal({ type: alert.type, propertyID: alert.propertyID }));
      setActiveTab('active');
    },
    [dispatch, setActiveTab],
  );

  const clearCommentaryFocus = useCallback(() => setCommentaryFocusKey(null), []);

  const dismissedAlertKeys = useMemo(
    () =>
      Object.values(alerts)
        .flat()
        .filter(
          (alert) =>
            alert.status === 'dismissed' ||
            alert.status === 'resolved_pending_comment' ||
            pendingDismissals.some((entry) => dismissedAlertMatches(entry, alert)),
        )
        .map((alert) => getAlertCommentaryKey(alert)),
    [alerts, pendingDismissals],
  );

  const schDismissedAlertsCommentary = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          Object.values(alerts)
            .flat()
            .filter(
              (alert) =>
                alert.status === 'dismissed' ||
                alert.status === 'resolved_pending_comment' ||
                pendingDismissals.some((entry) => dismissedAlertMatches(entry, alert)),
            )
            .map((alert) => [
              getAlertCommentaryKey(alert),
              alert.severity === 'warning'
                ? z.string().optional()
                : z.string().min(5, 'This field is required'),
            ]),
        ),
      ),
    [alerts, pendingDismissals],
  );

  const zodForm = useZodForm({
    schema: schDismissedAlertsCommentary,
    defaultValues: {},
    mode: 'onChange',
  });

  const { getValues, watch, reset } = zodForm;
  const formValues = watch();
  const hasInitializedCommentaryRef = useRef(false);

  const mergedCommentary = useMemo((): AlertsCommentary | undefined => {
    const cleanedFormData = Object.entries(formValues)
      .map(([k, v]) => [k, trimAlertCommentaryFormValue(v)])
      .filter(([_k, v]) => v !== undefined) as [string, string][];
    const formCommentary = cleanedFormData.length ? Object.fromEntries(cleanedFormData) : undefined;
    if (!reduxCommentary && !formCommentary) {
      return undefined;
    }
    return { ...reduxCommentary, ...formCommentary };
  }, [formValues, reduxCommentary]);

  const pageGroups = useMemo(() => {
    const groups = getOrderedAlertPageGroups(alerts, routeToStepMap);
    return groups
      .map(({ page, alerts: pageAlerts }) => ({
        page,
        alerts: pageAlerts.filter((alert) =>
          alertMatchesReferencePanelTab(alert, activeTab, triggeredAlertTypes, {
            isInspectifyOrder: treatAsInspectifyOrder,
            alertsCommentary: mergedCommentary,
            explanationContext,
            currentRoute,
            routeToStepMap,
          }),
        ),
      }))
      .filter(({ alerts: pageAlerts }) => pageAlerts.length > 0);
  }, [
    activeTab,
    alerts,
    currentRoute,
    explanationContext,
    mergedCommentary,
    treatAsInspectifyOrder,
    triggeredAlertTypes,
  ]);

  const autoSwitchedFocusKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focusedAlertType) {
      autoSwitchedFocusKeyRef.current = null;
      return;
    }

    const focusKey = `${focusedAlertType}:${focusedAlertPropertyID ?? ''}:${focusedAlertTargetCellKey ?? ''}`;
    if (autoSwitchedFocusKeyRef.current === focusKey) {
      return;
    }
    autoSwitchedFocusKeyRef.current = focusKey;

    const focusedAlert = Object.values(alerts)
      .flat()
      .find((alert) =>
        alertMatchesFocusIdentity(alert, {
          type: focusedAlertType,
          propertyID: focusedAlertPropertyID,
          targetCellKey: focusedAlertTargetCellKey,
        }),
      );
    if (!focusedAlert) {
      return;
    }

    if (pendingDismissals.some((entry) => dismissedAlertMatches(entry, focusedAlert))) {
      setActiveTab('active');
      return;
    }

    setActiveTab(getAlertReferencePanelTab(focusedAlert, mergedCommentary, explanationContext));
  }, [
    alerts,
    explanationContext,
    focusedAlertPropertyID,
    focusedAlertTargetCellKey,
    focusedAlertType,
    mergedCommentary,
    pendingDismissals,
    setActiveTab,
  ]);

  const hasHydratedFinalizedRef = useRef(false);

  useEffect(() => {
    if (!isCommentaryLoaded || hasHydratedFinalizedRef.current) {
      return;
    }
    hasHydratedFinalizedRef.current = true;
    Object.values(alerts)
      .flat()
      .forEach((alert) => {
        if (
          !alertRequiresExplanationFinalization(alert) ||
          !dismissedAlerts.some((entry) => dismissedAlertMatches(entry, alert)) ||
          !alertHasResolvedComment(alert, reduxCommentary) ||
          finalizedExplanations.some((entry) => dismissedAlertMatches(entry, alert))
        ) {
          return;
        }
        dispatch(addFinalizedExplanation({ type: alert.type, propertyID: alert.propertyID }));
      });
  }, [
    alerts,
    dismissedAlerts,
    dispatch,
    finalizedExplanations,
    isCommentaryLoaded,
    reduxCommentary,
  ]);

  useEffect(() => {
    if (!isCommentaryLoaded || hasInitializedCommentaryRef.current) {
      return;
    }
    hasInitializedCommentaryRef.current = true;
    reset(
      buildAlertsCommentaryFormValues(
        Object.values(alerts).flat(),
        selectCommentaryV2(store.getState()).alerts,
      ),
    );
  }, [alerts, isCommentaryLoaded, reset]);

  useEffect(() => {
    if (!isCommentaryLoaded || !hasInitializedCommentaryRef.current) {
      return;
    }
    reset(
      buildAlertsCommentaryFormValues(
        Object.values(alerts).flat(),
        selectCommentaryV2(store.getState()).alerts,
      ),
      { keepDirtyValues: true },
    );
  }, [alerts, dismissedAlertKeys, isCommentaryLoaded, reset]);

  const debouncedSetCommentary = useMemo(
    () =>
      debounce((newAlertsCommentary: AlertsCommentary | undefined) => {
        const commentaryV2 = selectCommentaryV2(store.getState());
        if (isEqual(newAlertsCommentary, commentaryV2.alerts)) return;

        dispatch(
          setCommentaryV2({
            ...commentaryV2,
            alerts: newAlertsCommentary,
          }),
        );
      }, 300),
    [dispatch],
  );

  useEffect(() => () => debouncedSetCommentary.cancel(), [debouncedSetCommentary]);

  const handleFinalizeExplanation = useCallback(
    (alert: Alert) => {
      debouncedSetCommentary.flush();
      const alertCommentaryKey = getAlertCommentaryKey(alert);
      const commentaryV2 = selectCommentaryV2(store.getState());
      const nextAlerts = finalizeAlertCommentaryValue(
        commentaryV2.alerts,
        alertCommentaryKey,
        getValues(alertCommentaryKey),
      );
      if (!isEqual(nextAlerts, commentaryV2.alerts)) {
        dispatch(
          setCommentaryV2({
            ...commentaryV2,
            alerts: nextAlerts,
          }),
        );
      }
      dispatch(addFinalizedExplanation({ type: alert.type, propertyID: alert.propertyID }));
      dispatch(dismissAlert({ type: alert.type, propertyID: alert.propertyID }));
      dispatch(removePendingDismissal({ type: alert.type, propertyID: alert.propertyID }));
    },
    [debouncedSetCommentary, dispatch, getValues],
  );

  const handleUndoExplanation = useCallback(
    (alert: Alert) => {
      dispatch(removeFinalizedExplanation({ type: alert.type, propertyID: alert.propertyID }));
      dispatch(cancelDismissAlert({ type: alert.type, propertyID: alert.propertyID }));
    },
    [dispatch],
  );

  useEffect(() => {
    zodForm.trigger();
  }, [dismissedAlertKeys, zodForm]);

  useEffect(() => {
    if (!isCommentaryLoaded || !hasInitializedCommentaryRef.current) {
      return;
    }
    const subscription = watch((formValues) => {
      const existingAlerts = selectCommentaryV2(store.getState()).alerts ?? {};
      const merged: AlertsCommentary = { ...existingAlerts };

      for (const [key, value] of Object.entries(formValues)) {
        const trimmed = trimAlertCommentaryFormValue(value);
        if (trimmed === undefined) {
          delete merged[key];
        } else {
          merged[key] = trimmed;
        }
      }

      debouncedSetCommentary(Object.keys(merged).length > 0 ? merged : undefined);
    });

    return () => subscription.unsubscribe();
  }, [debouncedSetCommentary, isCommentaryLoaded, watch]);

  if (pageGroups.length === 0) {
    return <Typography>{EMPTY_TAB_MESSAGES[activeTab]}</Typography>;
  }

  return (
    <FormProvider {...zodForm}>
      {pageGroups.map(({ page, alerts: pageAlerts }) => (
        <Flex key={page} flexDirection="column" gap="16px" width="100%">
          <Typography variant="h3">{getAlertPageLabel(page)}</Typography>
          <Flex flexDirection="column" gap="16px" width="100%">
            {pageAlerts.map((alert) => {
              const alertCommentaryKey = getAlertCommentaryKey(alert);
              const isPendingExplain = pendingDismissals.some((entry) =>
                dismissedAlertMatches(entry, alert),
              );
              return (
                <AlertItem
                  key={alertCommentaryKey}
                  alert={alert}
                  shouldFocusCommentary={commentaryFocusKey === alertCommentaryKey}
                  onCommentaryFocused={clearCommentaryFocus}
                  onExplain={() => handleExplain(alert)}
                  isPendingExplain={isPendingExplain}
                  isExplanationFinalized={isAlertExplanationFinalizedForTab(
                    alert,
                    explanationContext,
                  )}
                  onFinalizeExplanation={() => handleFinalizeExplanation(alert)}
                  onCancelExplain={() => handleCancelExplain(alert)}
                  onUndoExplanation={() => handleUndoExplanation(alert)}
                />
              );
            })}
          </Flex>
        </Flex>
      ))}
    </FormProvider>
  );
};
